import {
  DropboxOutlined,
  FileImageOutlined,
  FileProtectOutlined,
  FundProjectionScreenOutlined,
  LogoutOutlined, MenuFoldOutlined, MenuUnfoldOutlined,  UserOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import {Button, ConfigProvider, Divider, Layout, Menu, theme} from 'antd';
import {jwtDecode} from 'jwt-decode';
import React, {Suspense, lazy, useEffect, useRef, useState} from 'react';
import {BiCategory} from 'react-icons/bi';
import {HiOutlineOfficeBuilding} from 'react-icons/hi';
import {RiCoupon3Line} from 'react-icons/ri';
import {useDispatch, useSelector} from 'react-redux';
import {NavLink, Route, Routes, useLocation, useNavigate} from 'react-router-dom';

import Collapselogo from '../../assets/collapsedlogo.png';
import logo from '../../assets/logo.png';
import {RQGetProfileAPI} from '../../reactQuery/apis';
import {logoutStart} from '../../redux/authAction';
import {selectAuth} from '../../redux/selectors';

import DrawerMenu from './DrawerMenu/DrawerMenu';
import HomeImages from './HomeImages/HomeImages';
import {MainDashboard} from './MainDashboard';
import {Order} from './Order';
import Product from './Product/Product';
import {ProductDetailScreen} from './Product/ProductDetailScreen';
import {ProductEditScreen} from './Product/ProductEditScreen';
import {ProductBrandScreen} from './ProductBrand';
import {ProductCategoryScreen} from './ProductCategory';
import ProductPromocode from './ProductPromoCode/ProductPromocode';
import {ProfileScreen} from './ProfileScreen';
import {SingleUser} from './SingleUser';
import {EditProfile} from './SingleUser/EditProfile';
import {OrderSummary} from './SingleUser/OrderSummary';
import {UserHistory} from './SingleUser/UserHistory';
import {UserDetails} from './UserDetails';

const {Header, Content,  Sider} = Layout;

function getItem(label, key, icon, path, children) {
  return {
    key,
    icon,
    label,
    path,
    children,
  };
}

const AdminDashboard = () => {
  const [collapsed, setCollapsed] = useState(false);

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const location = useLocation();

  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const decodedToken = token ? jwtDecode(token) : {};

  const {data: userProfileDetails} = RQGetProfileAPI(decodedToken?.userId, token);

  const items = [
    getItem('Dashboard', '1', <FundProjectionScreenOutlined />, '/admin/dashboard'),
    getItem('Users', '2', <UsergroupAddOutlined />, '/admin/user'),
    getItem('Orders', '4', <FileProtectOutlined />, '/admin/order'),
    getItem('Product', 'sub1', <DropboxOutlined />, '/admin/team/product', [
      getItem('Product', '3', null, '/admin/team/product'),
    ]),
    getItem('Product Category', '5', <BiCategory />, '/admin/productCategory'),
    getItem('Product Brand', '6', <HiOutlineOfficeBuilding />, '/admin/productBrand'),
    getItem('Promocode', '7', <RiCoupon3Line />, '/admin/Prommocode'),
    getItem('Images', '7', <FileImageOutlined />, '/admin/images'),
    getItem('Profile', '8', <UserOutlined />, '/admin/profile'),
  ];
  const skipRedirectRef = useRef(false);

  useEffect(() => {
    if (!auth.loggedIn) {
      dispatch(logoutStart({showLoginModal: true}));
    }
  }, [auth.loggedIn, dispatch]);

  const {
    token: {colorBgContainer, borderRadiusLG},
  } = theme.useToken();

  const handleLogout = () => {
    skipRedirectRef.current = true;
    dispatch(logoutStart());
    navigate('/');
  };

  return (
    <Layout className={'min-h-screen'}>
      <Sider
        trigger={null}
        className={'h-screen overflow-y-auto lg:block hidden sticky top-0 left-0  bg-white text-black  shadow-right shadow'}

        collapsible collapsed={collapsed}>
        <div className={'demo-logo-vertical'} />
        <div className={'flex items-center justify-center h-24 text-2xl my-2'}>
          {collapsed ? (
            <NavLink to={'/'}>
              <img
                src={Collapselogo}
                alt={'Logo'}
                className={ 'md:h-14 h-8'}
              />
            </NavLink>
          ) : (
            <NavLink to={'/'}>
              <img
                src={logo}
                alt={'Logo'}
                className={ 'md:h-14 h-8'}
              />
            </NavLink>
          )}
        </div>
        <Divider className={'my-2'} />
   
        <Menu mode={'inline'} className={'custom-menu'} selectedKeys={[location.pathname]}>
          {items.map((item, index) => (
            <React.Fragment key={index}>
              <Menu.Item
                key={item.path}
                icon={item.icon}
                onClick={() => {
                  navigate(item.path);
                }}
              >
                {item.label}
              </Menu.Item>
              {index !== items.length - 1 && (
                <Divider className={'my-2 border-gray-300'} />
              )}
            </React.Fragment>
          ))}
        </Menu>
      </Sider>

      <Layout>
        <Header
          className={'sticky top-0 z-50 flex items-center justify-between px-4  shadow-inner'}
          style={{background: colorBgContainer, height: 64}}
        >
          {/* Sidebar toggle button for desktop */}
          <Button
            type={'text'}
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className={'text-xl lg:block hidden'}
          />

          {/* Drawer menu for mobile */}
          <div className={'lg:hidden block'}>
            <DrawerMenu items={items} />
          </div>

          {/* Welcome text */}
          <div className={'flex items-center gap-3'}>
            <h1 className={'text-lg '}>
      Welcome,&nbsp;
              <span className={'text-primary-900'}>{userProfileDetails?.firstName}</span>
              <span className={'text-xs text-gray-500'}> ({decodedToken.role})</span>
            </h1>

            {/* Logout button */}
            <Button
              onClick={handleLogout}
              className={'flex items-center gap-1 !bg-primary-900 !text-white'}
            >
              <LogoutOutlined />
            </Button>
          </div>
        </Header>

        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <ConfigProvider
            theme={{
              token: {
                colorPrimary: '#0a2a74',
              },
            }}
          >
            <Routes path={'/admin'}>
              <Route path={'/dashboard'} element={<MainDashboard />} />
              <Route path={'/user'} element={<UserDetails />} />
              <Route path={'/profile'} element={<ProfileScreen />} />
              <Route path={'/team/product'} element={<Product />} />
              <Route path={'/productDetail'} element={<ProductDetailScreen />} />
              <Route path={'/productEdit'} element={<ProductEditScreen />} />
              <Route path={'/productBrand'} element={<ProductBrandScreen />} />
              <Route path={'/productCategory'} element={<ProductCategoryScreen />} />
              <Route path={'/Prommocode'} element={<ProductPromocode />} />
              <Route path={'/images'} element={<HomeImages />} />              
              <Route path={'/order'} element={<Order />} />
              <Route path={'/user/:id'} element={<SingleUser />} />
              <Route path={'/edituser'} element={<EditProfile />} />
              <Route path={'/uhistory/:id'} element={<UserHistory />} />
              <Route path={'/order/:id'} element={<OrderSummary />} />
            </Routes>
          </ConfigProvider>
        </Content>     
      </Layout>
    </Layout>
  );
};

export default AdminDashboard;
